/**
 * `<AnnotationStyleBar>` — restyle the drawing that is selected.
 *
 * A drawing arrived with the theme's defaults and stayed that way: the contract
 * has carried `Annotation.style` (stroke, fill, opacity, thickness, dash,
 * fontSize, fontColor) since the beginning, the render plan has always resolved
 * both colours through the active theme, and the SciChart shell has always
 * applied all seven. Nothing could *set* them, so the whole branch was reachable
 * only by hand-editing a workspace. This is the missing control, and it is only
 * a control — no contract change, no renderer change.
 *
 * **Floating over the chart, and only while something is selected.** It is about
 * one drawing rather than about the chart, so it cannot live in the tool strip,
 * which is always there and belongs to the chart. Appearing with the selection is
 * also the only affordance that teaches a drawing *can* be restyled: nothing else
 * on screen says so.
 *
 * **One drawing at a time.** SciChart will happily select several, and patching
 * them together would be easy, but a bar showing "the" colour of four drawings in
 * three colours has to either lie or show nothing. With more than one selected it
 * says how many and offers nothing, which is honest and rare.
 *
 * **It writes a token, never a hex, unless the user asks for one.** That is the
 * whole reason a drawing follows a theme switch today, and the escape hatch is
 * marked where it is offered — see `ColorSelect`.
 */

import type { Annotation, PlotStyle } from "@qufolio/model";
import { BAR_HIGHLIGHT_FILL, TRADING_FILLABLE, plotDashFor } from "@qufolio/chart-engine";

import { ColorSelect, NO_COLOR } from "./ColorSelect";
import { PANE_TOOLBAR_BOTTOM } from "./PaneReorder";

type DashStyle = NonNullable<PlotStyle["dashStyle"]>;

const DASH_STYLES: { id: DashStyle; label: string }[] = [
  { id: "solid", label: "Solid" },
  { id: "dashed", label: "Dashed" },
  { id: "dotted", label: "Dotted" },
];

/**
 * Which named dash an array came from, or `solid` for none.
 *
 * The stored value is the array, because that is what a renderer draws with and
 * the contract says so. The control offers the three names, so the current value
 * has to be recognised on the way back in — by comparing against the arrays the
 * names produce rather than by storing the name beside them, which would be a
 * second source of truth that a hand-edited workspace immediately falsifies.
 */
function dashStyleOf(dash: readonly number[] | undefined): DashStyle {
  if (!dash || dash.length === 0) return "solid";
  const match = DASH_STYLES.find(
    (d) => JSON.stringify(plotDashFor(d.id) ?? []) === JSON.stringify(dash),
  );
  // An array none of the three can say is left as it is and reported as the
  // nearest thing — the escape hatch stays usable, and choosing a name is what
  // overwrites it.
  return match?.id ?? "dashed";
}

/** Text is the only kind with a font size to set. */
const HAS_FONT = new Set<Annotation["type"]>(["text"]);

/**
 * The kinds with an interior to paint.
 *
 * A box is the one the contract, the plan and `BoxAnnotation` all already carry
 * `fill` for — every layer supported it and only the control was missing. A
 * line has no inside, and offering a fill for one is a control that does
 * nothing when used.
 *
 * The palette's kinds are added from `TRADING_FILLABLE` rather than listed
 * here, because which of them has an inside is a fact about the vendor's
 * classes: three channels, the cyclic arcs and the polyline declare `fill` and
 * the rest ignore it. A pitchfork looks like it should be in this set and is
 * not — its shading is `fullWidthZoneFill`, a different property.
 */
const HAS_FILL = new Set<Annotation["type"]>(["box", ...TRADING_FILLABLE]);

export interface AnnotationStyleBarProps {
  /** The drawings currently selected — ids, resolved against the panes here. */
  selected: readonly Annotation[];
  resolve: (token: string) => string;
  onStyle: (annotationId: string, style: NonNullable<Annotation["style"]>) => void;
  /** Removes one drawing; offered as 🗑 on a highlighted bar, which has nothing else. */
  onDelete?: (annotationId: string) => void;
}

export function AnnotationStyleBar({ selected, resolve, onStyle, onDelete }: AnnotationStyleBarProps) {
  if (selected.length === 0) return null;

  if (selected.length > 1) {
    return (
      <div style={RAIL}>
        <div style={BAR}>
          <span style={COUNT}>{selected.length} drawings selected</span>
        </div>
      </div>
    );
  }

  const annotation = selected[0]!;
  const style = annotation.style ?? {};
  const patch = (next: NonNullable<Annotation["style"]>) => onStyle(annotation.id, next);

  // **A highlighted bar: its colour and a delete, nothing else** (owner,
  // 2026-10-13). It has no line, so no width or dash, and it cannot be moved.
  if (annotation.type === "barHighlight") {
    return (
      <div style={RAIL}>
        <div style={BAR} role="group" aria-label="Highlight bar">
          <ColorSelect
            label="Highlight colour"
            value={style.fill ?? BAR_HIGHLIGHT_FILL}
            resolve={resolve}
            onChange={(token) => token && patch({ fill: token })}
          />
          {onDelete && (
            <button
              type="button"
              aria-label="Delete highlight"
              title="Delete highlight (Delete key)"
              onClick={() => onDelete(annotation.id)}
              style={DELETE}
            >
              🗑
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={RAIL}>
      <div style={BAR} role="group" aria-label="Drawing style">
        <ColorSelect
          label="Drawing colour"
          // `text.secondary` is what an unstyled drawing actually resolves to, so
          // the control opens on the colour on screen rather than on a blank.
          value={style.stroke ?? "text.secondary"}
          resolve={resolve}
          onChange={(token) => patch({ stroke: token })}
        />

        {HAS_FILL.has(annotation.type) && (
          <ColorSelect
            label="Fill colour"
            // **`None` is the default, and it is a real choice.** An unfilled
            // box is an outline — a zone marked without hiding the bars inside
            // it — which is what most people draw. A box that arrived filled by
            // default would cover the price action it was drawn to point at.
            value={style.fill ?? NO_COLOR}
            resolve={resolve}
            allowNone
            onChange={(token) => patch({ fill: token || undefined })}
          />
        )}

        <label style={FIELD}>
          <span style={LABEL}>Width</span>
          <input
            type="number"
            min={1}
            max={10}
            step={0.5}
            aria-label="Drawing width"
            value={style.thickness ?? 2}
            onChange={(e) => {
              const thickness = Number(e.target.value);
              // Guarded rather than clamped: a half-typed box is empty, and `NaN`
              // written into the workspace is a drawing that stops rendering.
              if (Number.isFinite(thickness) && thickness > 0) patch({ thickness });
            }}
            style={NUMBER}
          />
        </label>

        <label style={FIELD}>
          <span style={LABEL}>Dash</span>
          <select
            aria-label="Drawing dash"
            value={dashStyleOf(style.dash)}
            onChange={(e) => patch({ dash: plotDashFor(e.target.value as DashStyle) ?? [] })}
            style={SELECT}
          >
            {DASH_STYLES.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </label>

        {HAS_FONT.has(annotation.type) && (
          <label style={FIELD}>
            <span style={LABEL}>Size</span>
            <input
              type="number"
              min={8}
              max={48}
              step={1}
              aria-label="Text size"
              value={style.fontSize ?? 12}
              onChange={(e) => {
                const fontSize = Number(e.target.value);
                if (Number.isFinite(fontSize) && fontSize > 0) patch({ fontSize });
              }}
              style={NUMBER}
            />
          </label>
        )}
      </div>
    </div>
  );
}

/**
 * The rail the bar is centred on: top-centre, above the chart and below the pane
 * dividers (6) — the same band the span controls use at the other end of the
 * pane, and for the same reason, that it is over the part of the plot usually
 * left empty.
 *
 * **Centred by flex, not by `transform`.** `translateX(-50%)` was the obvious way
 * and it broke the colour dropdown: a transformed element becomes the containing
 * block for `position: fixed` descendants, so the list — which measures its
 * trigger in *viewport* coordinates — had those coordinates reinterpreted against
 * the bar and opened somewhere else entirely. Nothing in the list is wrong; an
 * ancestor simply moved the origin under it.
 *
 * `translateX(-50%)` was the obvious way to centre the bar and it broke the
 * colour dropdown: a transformed element becomes the containing block for
 * `position: fixed` descendants, so the list — which measures the trigger in
 * *viewport* coordinates — had those coordinates reinterpreted against the bar
 * and opened somewhere else entirely. A full-width flex rail centres without a
 * transform, which is also how the span controls at the other end of the pane do
 * it.
 *
 * `pointer-events: none` on the rail and `auto` on the bar, for the span
 * controls' reason too: it spans the chart, and a strip that swallowed a pan
 * would be worse than no strip.
 *
 * The bar itself is solid rather than translucent, unlike those — this is a panel
 * you work in, not one you glance at, and a colour swatch read through 45%
 * opacity is not a colour.
 */
const RAIL: React.CSSProperties = {
  position: "absolute",
  // **Below the pane reorder strip, not on top of it.** Both are centred at the
  // top of the pane — that edge is the quiet one, which is why they both chose
  // it — and at `top: 6` this sat directly over the ▲▼ buttons. The offset is
  // the strip's own height rather than a number picked by eye, so the two
  // cannot drift back into each other when it changes.
  top: PANE_TOOLBAR_BOTTOM + 6,
  left: 0,
  right: 0,
  zIndex: 5,
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
};

const BAR: React.CSSProperties = {
  pointerEvents: "auto",
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "4px 8px",
  borderRadius: 6,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
  boxShadow: "0 4px 14px rgba(0,0,0,0.28)",
};

const FIELD: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  color: "var(--qf-text-secondary)",
};

const LABEL: React.CSSProperties = { whiteSpace: "nowrap" };

const NUMBER: React.CSSProperties = {
  width: 46,
  font: "inherit",
  fontSize: 11,
  padding: "2px 4px",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  outline: "none",
};

const SELECT: React.CSSProperties = {
  font: "inherit",
  fontSize: 11,
  padding: "2px 3px",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  outline: "none",
};

const COUNT: React.CSSProperties = {
  fontSize: 11,
  color: "var(--qf-text-muted)",
};

const DELETE: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  padding: "1px 6px",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  cursor: "pointer",
};
